// @ts-strict-ignore
import { getNumericPathIndices, getParentRulePathIndicies } from '~/dashboard/views/workflows/service/actions';
import { createStringOfPathArray, getExpressionFunction } from '~/dashboard/views/workflows/store/engine/utils';
import { CaretPositionSuffixTypes, RuleEditorDropdownMode } from '../../NodeEditorDrawer/types';
import { getProperty } from 'dot-prop';
import { populateDataRouteData } from '~/dashboard/views/workflows/service/dataRoutes';
import type {
  Action, DataRoute, Expression, ServiceAttributesById, ServicesByName,
} from '~/dashboard/views/workflows/store/engine/types';

/**
 * Expects the path of a group itself, not of an item inside one.
 */
export const getFunctionForExpressionGroup = ({
  expressions,
  path,
}: {
  expressions: Expression;
  path: string;
}) => {
  const group = getProperty(expressions, path);
  return Array.isArray(group) ? getExpressionFunction(group) : undefined;
};

/**
 * The path of the min/max group a position sits inside. Accepts either a caret position (0-2!caret)
 * or an expression path ([0][2]), since both start with the index.
 */
export const getEnclosingFunctionGroupPath = ({
  expressions,
  position,
}: {
  expressions?: Expression;
  position?: string;
}) => {
  if (!expressions || !position) return undefined;

  const indices = getNumericPathIndices(position);
  // a single index is a top level position, which has no enclosing group
  if (!indices || indices.length < 2) return undefined;

  // walk outward, so a min/max nested inside an ordinary group is found first
  for (let depth = indices.length - 1; depth > 0; depth -= 1) {
    const groupPath = createStringOfPathArray(indices.slice(0, depth));

    if (getFunctionForExpressionGroup({ expressions, path: groupPath })) return groupPath;
  }

  return undefined;
};

/**
 * True when a caret position is a min/max group's closing parenthesis. Used to work out what
 * backspace deletes from there.
 */
export const getIsFunctionGroupClosing = ({
  expressions,
  position,
}: {
  expressions?: Expression;
  position?: string;
}) => {
  if (!expressions || !position
    || !position.endsWith(CaretPositionSuffixTypes.GROUP_END)) return false;

  return Boolean(getFunctionForExpressionGroup({
    expressions,
    path: createStringOfPathArray(getNumericPathIndices(position)),
  }));
};

/**
 * True when the item at this path is followed by its group's min/max, which is drawn as a comma
 * rather than as an operator.
 */
export const getIsFollowedByComma = ({
  expressions,
  path,
}: {
  expressions: Expression;
  path: string;
}) => {
  const indices = getNumericPathIndices(path);
  if (!indices || indices.length < 2) return false;

  const parentPath = createStringOfPathArray(indices.slice(0, -1));
  const parentGroup = getProperty(expressions, parentPath) as Expression;
  if (!Array.isArray(parentGroup)) return false;

  const groupFunction = getExpressionFunction(parentGroup);
  return Boolean(groupFunction)
    && parentGroup[Number(indices.at(-1)) + 1] === groupFunction;
};

export const getExpressionPathInDotNotationFormat = ({
  expressions,
  itemFlatCaret,
  workflowInfo: {
    actions,
    dataRoutes,
    services,
    universalAttributes,
  },
}: {
  expressions: Expression;
  itemFlatCaret: any;
  workflowInfo: {
    actions: Record<string, Action>;
    dataRoutes: Record<string, DataRoute>;
    services: ServicesByName;
    universalAttributes: ServiceAttributesById;
  };
}) => {
  const pathIndices = getNumericPathIndices(itemFlatCaret);
  if (!pathIndices) return undefined;

  const path = createStringOfPathArray(pathIndices);

  const groupingsMap = {
    [CaretPositionSuffixTypes.GROUP_START]: RuleEditorDropdownMode.GROUP_START,
    [CaretPositionSuffixTypes.GROUP_START_PENDING]: RuleEditorDropdownMode.GROUP_START_PENDING,
    [CaretPositionSuffixTypes.GROUP_END]: RuleEditorDropdownMode.GROUP_END,
  };

  for (const [caretSuffix, mode] of Object.entries(groupingsMap)) {
    if (itemFlatCaret.endsWith(caretSuffix)) {
      /*
        A min/max opening is still a group opening, and behaves like one for caret indexing, navigation
        and deletion. Only the drawing differs, so it carries the group's operator as data.
      */
      return {
        path,
        type: mode,
        data: mode === RuleEditorDropdownMode.GROUP_START
          ? getFunctionForExpressionGroup({ expressions, path })
          : undefined,
      };
    }
  }

  if (itemFlatCaret.endsWith(CaretPositionSuffixTypes.DATAROUTE)) {
    const parentPathIndicies = getParentRulePathIndicies(pathIndices);
    const parentPathString = createStringOfPathArray(parentPathIndicies);
    const parentData = getProperty(expressions, parentPathString, { data_route_id: null });

    if (parentData.data_route_id) {
      const extendedDataRoute = populateDataRouteData({
        actions,
        services,
        dataRoute: dataRoutes[parentData.data_route_id],
        universalAttributes,
      });

      const pathIndicesMap = {
        0: RuleEditorDropdownMode.DATAROUTE_SOURCE,
        1: RuleEditorDropdownMode.DATAROUTE_ATTRIBUTE,
      };

      return {
        path: parentPathString,
        type: pathIndicesMap[pathIndices.at(-1)] || RuleEditorDropdownMode.EXPRESSION,
        data: extendedDataRoute,
      };
    }

    return {
      path,
      type: RuleEditorDropdownMode.EXPRESSION,
      data: parentData,
    };
  }

  return {
    path,
    type: RuleEditorDropdownMode.EXPRESSION,
    data: getProperty(expressions, path, undefined),
  };
};
